import React, { useContext, useState } from "react";
import { find, isEmpty, get } from "lodash";
import { Field, Formik } from "formik";
import * as Yup from "yup";
import { Row, Col, Form, Modal, Button } from "react-bootstrap";
import { faPencilAlt } from "@fortawesome/pro-light-svg-icons";
import { faExclamationTriangle } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faTrashAlt } from "@fortawesome/pro-light-svg-icons";
import {
  ROUTES,
  MANAGE_COMPANY_ROUTES,
  manageCompanyFormNames,
  formFields,
  inverseYesNoOptions,
  FLOW_TYPES,
  getAdvancedPathWithParam,
  returnOnlyIfBoolean,
  required,
  clearFieldValues,
  usDateFormat,
} from "../../utils";
import {
  ManageCompanyLayout,
  SearchableList,
  DayDropdown,
  FieldDropSide,
  FieldButtonGroup,
  FieldInput,
  DeleteWarning,
  DatePicker,
} from "../../components";
import { useRouterParams } from "../../abstracts";
import {
  manageCompanyStore,
  setManageCompanyToastInfo,
  saveCompanyDetails,
} from "../../contexts";
import payrollFrequency from "../../mocks/payrollFrequency.json";
import weekdays from "../../mocks/weekdays.json";
import months from "../../mocks/months.json";
import days from "../../mocks/days.json";

const PayrollCalendarContainer = (props) => {
  const { history } = props;
  const { companyId, flow, frequencyId } = useRouterParams();
  const intFrequencyId = parseInt(frequencyId, 10);

  const { state, dispatch } = useContext(manageCompanyStore);
  const formName = manageCompanyFormNames.PAYROLL_FREQUENCY_MANAGE_COMPANY;
  const payrollFrequencyListData = get(
    state,
    "api.data.payrollFrequencies",
    []
  );
  const formValues = find(payrollFrequencyListData, {
    id: intFrequencyId,
  });
  const fields = formFields[formName];
  const [isModalOpen, setIsModalOpen] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");

  const [showWarning, setShowWarning] = useState(false);
  const DeleteToastMessage = "You are attempting to delete payroll frequency. ";
  const DeleteToastMessageHead = "Delete Payroll Frequency?";
  const handleClose = () => {
    setIsModalOpen(false);
  };

  const getDataForSave = (values) => {
    if (isEmpty(formValues)) {
      return [
        ...payrollFrequencyListData,
        {
          ...values,
        },
      ];
    }

    return payrollFrequencyListData.map((item) => {
      if (item.id === intFrequencyId) {
        return { ...item, ...values };
      }
      return item;
    });
  };

  const onFormSubmit = (values, { setFieldError, setFieldTouched }) => {
    saveCompanyDetails(
      {
        payrollFrequencies: getDataForSave(values),
      },
      dispatch,
      state
    ).then((response) => {
      if (!response.isSuccessful) {
        for (var i = 0; i < response.errorMessages.length; i++) {
          var _ = response.errorMessages[i];
          setFieldTouched(_.controlName, _.message);
          setFieldError(_.controlName, `${_.errorCode} : ${_.message}`);
        }
      } else {
        history.push(
          getAdvancedPathWithParam({
            path: MANAGE_COMPANY_ROUTES.MANAGE_PAYROLL_FREQUENCY,
            pathParam: [get(state, "flow"), companyId],
          })
        );
        dispatch(
          setManageCompanyToastInfo({
            showToast: true,
            toastMessage: "Data saved successfully",
          })
        );
      }
    });
  };

  const isEdit = flow === FLOW_TYPES.EDIT;
  const isSave = flow === FLOW_TYPES.SAVE;
  const isAdd = flow === FLOW_TYPES.ADD;
  const isDisabled = frequencyId !== undefined;
  return (
    <Formik
      initialValues={{
        ...formValues,
      }}
      // validationSchema={schema}
      onSubmit={onFormSubmit}
      validateOnChange={false}
      validateOnBlur={false}
    >
      {({
        handleChange,
        setFieldValue,
        handleSubmit,
        values,
        setValues,
        setSubmitting,
        ...rest
      }) => {
        const onDaySelected = (fieldName, value) => {
          setFieldValue(fieldName, value);
        };
        const onFrequencyTypeChange = (value) => {
          const updatedValues = clearFieldValues({
            values,
            fieldsToExculde: ([fields.frequencyType], [fields.frequencyName]),
          });

          setValues({
            ...updatedValues,
            [fields.frequencyType]: value,
            [fields.frequencyName]:
              payrollFrequency.data.find((item) => item.value === value)
                ?.label || null,
          });

          setFieldValue(fields.worksOnSaturday, true);
          setFieldValue(fields.worksOnSunday, true);
          setFieldValue(fields.frequencyType, value);
        };
        const onDeleteClick = () => {
          setShowWarning(true);
        };
        const handleCancelClick = () => {
          setShowWarning(false);
        };
        const handleConfirmClick = () => {
          setShowWarning(false);
          setSubmitting(true);
          const payrollFrequencyFormValues = get(state, formName, {});
          saveCompanyDetails(
            {
              payrollFrequencies: payrollFrequencyListData.filter(
                ({ id }) => id !== intFrequencyId
              ),
            },
            dispatch,
            state
          ).then((response) => {
            if (!response.isSuccessful) {
              var x = response.errorMessages;
              if (
                x[0].controlName == null &&
                x[0].errorCode !== null &&
                x[0].message !== null
              ) {
                setErrorMsg(`${x[0].errorCode} : ${x[0].message}`);
                setIsModalOpen(true);
              }
            } else {
              dispatch(
                setManageCompanyToastInfo({
                  showToast: true,
                  toastMessage: "Data deleted successfully",
                })
              );
              history.push(
                getAdvancedPathWithParam({
                  path: MANAGE_COMPANY_ROUTES.MANAGE_PAYROLL_FREQUENCY,
                  pathParam: [flow, companyId],
                })
              );
            }
          });
        };
        const buttons = [
          {
            label: "Cancel",
            variant: "secondary",
            type: "button",
            flow: [FLOW_TYPES.ADD, FLOW_TYPES.SAVE],
            link: getAdvancedPathWithParam({
              path: MANAGE_COMPANY_ROUTES.MANAGE_PAYROLL_FREQUENCY,
              pathParam: [FLOW_TYPES.EDIT, companyId],
            }),
          },
          {
            label: "Save",
            variant: "primary",
            type: "submit",
            flow: [FLOW_TYPES.ADD, FLOW_TYPES.SAVE],
          },
          {
            label: "Cancel",
            variant: "secondary",
            type: "button",
            flow: [FLOW_TYPES.EDIT],
            link: getAdvancedPathWithParam({
              path: MANAGE_COMPANY_ROUTES.MANAGE_PAYROLL_FREQUENCY,
              pathParam: [flow, companyId],
            }),
          },
          {
            label: "Edit",
            variant: "primary",
            type: "button",
            flow: [FLOW_TYPES.EDIT],
            onClick: () =>
              history.push(
                getAdvancedPathWithParam({
                  path: MANAGE_COMPANY_ROUTES.PAYROLL_FREQUENCY,
                  pathParam: [FLOW_TYPES.SAVE, companyId, frequencyId],
                })
              ),
          },
          {
            label: "Delete",
            variant: "danger",
            type: "button",
            flow: [FLOW_TYPES.EDIT],
            onClick: onDeleteClick,
          },
          // {
          //   label: "",
          //   variant: "link",
          //   type: "button",
          //   flow: [FLOW_TYPES.EDIT],
          //   icon: faTrashAlt,
          //   onClick: onDeleteClick,
          // },
        ];

        return (
          <Form
            autoComplete="off"
            className="h-100"
            onSubmit={handleSubmit}
            validated={rest.submitCount > 0}
          >
            <ManageCompanyLayout buttons={buttons} pageFlow={flow}>
              <Row>
                <Col>
                  <Field
                    label="Payroll Frequency"
                    name={fields.frequencyType}
                    isRequired
                    value={values[fields.frequencyType]}
                    disabled={!isAdd}
                    options={payrollFrequency.data}
                    popupContent={
                      <SearchableList
                        label="Select a payroll frequency"
                        options={payrollFrequency.data}
                        onSelect={onFrequencyTypeChange}
                        selectedValue={values[fields.frequencyType]}
                        name={fields.frequencyType}
                        isNotTypeAhead
                        isRadio
                      />
                    }
                    component={FieldDropSide}
                  />
                  {values[fields.frequencyType] === 1 && (
                    <>
                      <Field
                        label="Exclude Saturdays?"
                        name={fields.worksOnSunday}
                        isRequired
                        size="sm"
                        options={inverseYesNoOptions}
                        selectedValue={returnOnlyIfBoolean(
                          values[fields.worksOnSaturday]
                        )}
                        onChange={(value) => {
                          setFieldValue(fields.worksOnSaturday, value);
                        }}
                        disabled={isEdit && !isSave}
                        component={FieldButtonGroup}
                      />
                      <Field
                        label="Exclude Sundays?"
                        name={fields.worksOnSunday}
                        isRequired
                        size="sm"
                        options={inverseYesNoOptions}
                        selectedValue={returnOnlyIfBoolean(
                          values[fields.worksOnSunday]
                        )}
                        onChange={(value) => {
                          setFieldValue(fields.worksOnSunday, value);
                        }}
                        disabled={isEdit && !isSave}
                        component={FieldButtonGroup}
                      />
                    </>
                  )}
                  {[2].includes(values[fields.frequencyType]) && (
                    <Field
                      label="Start day"
                      name={fields.startDay}
                      isRequired
                      value={values[fields.startDay]}
                      options={weekdays.data}
                      popupContent={
                        <SearchableList
                          label="Select day"
                          options={weekdays.data}
                          selectedValue={values[fields.startDay]}
                          onSelect={(value) =>
                            setFieldValue(fields.startDay, value)
                          }
                          isNotTypeAhead
                          isRadio
                        />
                      }
                      disabled={isEdit && !isSave}
                      component={FieldDropSide}
                    />
                  )}
                  {[8].includes(values[fields.frequencyType]) && (
                    <Field
                      name={fields.biWeeklyStartDate}
                      isRequired
                      label="Start Date"
                      value={usDateFormat(values[fields.biWeeklyStartDate])}
                      disabled={isEdit && !isSave}
                      isDatePicker
                      onClear={() =>
                        setFieldValue(fields.biWeeklyStartDate, null)
                      }
                      popupContent={
                        <DatePicker
                          onDayClick={(value) =>
                            onDaySelected(fields.biWeeklyStartDate, value)
                          }
                          value={usDateFormat(values[fields.biWeeklyStartDate])}
                        />
                      }
                      component={FieldDropSide}
                    />
                  )}
                  {[3].includes(values[fields.frequencyType]) && (
                    <>
                      <Field
                        label="First begin day"
                        name={fields.firstBeginDay}
                        size="sm"
                        isRequired
                        value={values[fields.firstBeginDay]}
                        options={days.data}
                        popupContent={
                          <DayDropdown
                            fromMonthDate={new Date(2020, 0)}
                            onSelect={(value) =>
                              setFieldValue(
                                fields.firstBeginDay,
                                parseInt(value, 10)
                              )
                            }
                            value={values[fields.firstBeginDay]}
                          />
                        }
                        disabled={isEdit && !isSave}
                        component={FieldDropSide}
                      />
                      <Field
                        label="Second begin day"
                        name={fields.secondBeginDay}
                        size="sm"
                        isRequired
                        value={values[fields.secondBeginDay]}
                        options={days.data}
                        popupContent={
                          <DayDropdown
                            fromMonthDate={new Date(2020, 0)}
                            onSelect={(value) =>
                              setFieldValue(
                                fields.secondBeginDay,
                                parseInt(value, 10)
                              )
                            }
                            value={values[fields.secondBeginDay]}
                          />
                        }
                        disabled={isEdit && !isSave}
                        component={FieldDropSide}
                      />
                    </>
                  )}
                  {[4].includes(values[fields.frequencyType]) && (
                    <Field
                      label="Starting day of every month"
                      name={fields.startDate}
                      size="sm"
                      isRequired
                      value={values[fields.startDate]}
                      options={days.data}
                      popupContent={
                        <DayDropdown
                          fromMonthDate={new Date(2020, 0)}
                          onSelect={(value) =>
                            setFieldValue(fields.startDate, parseInt(value, 10))
                          }
                          value={values[fields.startDate]}
                        />
                      }
                      disabled={isEdit && !isSave}
                      component={FieldDropSide}
                    />
                  )}
                  {[5].includes(values[fields.frequencyType]) && (
                    <Field
                      label="Starting month for first quarter"
                      name={fields.startMonth}
                      isRequired
                      value={values[fields.startMonth]}
                      options={months.data}
                      popupContent={
                        <SearchableList
                          label="Select a month"
                          options={months.data}
                          onSelect={(value) =>
                            setFieldValue(fields.startMonth, value)
                          }
                          isNotTypeAhead
                          selectedValue={values[fields.startMonth]}
                        />
                      }
                      disabled={isEdit && !isSave}
                      component={FieldDropSide}
                    />
                  )}
                  {[6].includes(values[fields.frequencyType]) && (
                    <Field
                      label="Starting month for the half-year"
                      name={fields.startMonth}
                      isRequired
                      value={values[fields.startMonth]}
                      options={months.data}
                      popupContent={
                        <SearchableList
                          label="Select a month"
                          options={months.data}
                          onSelect={(value) =>
                            setFieldValue(fields.startMonth, value)
                          }
                          isNotTypeAhead
                          selectedValue={values[fields.startMonth]}
                        />
                      }
                      disabled={isEdit && !isSave}
                      component={FieldDropSide}
                    />
                  )}
                  {[7].includes(values[fields.frequencyType]) && (
                    <div className="d-flex">
                      <Field
                        label="Starting date"
                        name={fields.startMonth}
                        isRequired
                        size="sm"
                        value={values[fields.startMonth]}
                        options={months.data}
                        placeholder="Month"
                        popupContent={
                          <SearchableList
                            label="Select a month"
                            options={months.data}
                            onSelect={(value) =>
                              setFieldValue(fields.startMonth, value)
                            }
                            isNotTypeAhead
                            selectedValue={values[fields.startMonth]}
                          />
                        }
                        disabled={isEdit && !isSave}
                        component={FieldDropSide}
                      />
                      &nbsp; &nbsp;
                      <Field
                        label=" "
                        size="xs"
                        name={fields.startDate}
                        value={values[fields.startDate]}
                        placeholder="day"
                        popupContent={
                          <DayDropdown
                            fromMonthDate={
                              new Date(2020, values[fields.startMonth] - 1 || 0)
                            }
                            onSelect={(value) =>
                              setFieldValue(
                                fields.startDate,
                                parseInt(value, 10)
                              )
                            }
                            value={values[fields.startDate]}
                          />
                        }
                        disabled={isEdit && !isSave}
                        component={FieldDropSide}
                      />
                    </div>
                  )}
                  <Field
                    label="Frequency name"
                    name={fields.frequencyName}
                    isRequired
                    type="text"
                    value={values[fields.frequencyName]}
                    onChange={handleChange}
                    autoComplete="nope"
                    disabled={isEdit && !isSave}
                    component={FieldInput}
                  />
                </Col>
              </Row>
              <DeleteWarning
                DeleteToastMessageHead={DeleteToastMessageHead}
                DeleteToastMessage={DeleteToastMessage}
                showModal={showWarning}
                handleConfirmClick={handleConfirmClick}
                handleCancelClick={handleCancelClick}
              />
              <Modal show={isModalOpen} onHide={handleClose}>
                <Modal.Body style={{ borderTop: "5px solid #f94f50" }}>
                  <div className="text-right">
                    <FontAwesomeIcon
                      icon={faTimes}
                      color="#000"
                      onClick={handleClose}
                    />
                  </div>
                  <div className="d-flex">
                    <div className="pd-15">
                      <FontAwesomeIcon
                        icon={faExclamationTriangle}
                        color="#f94f50"
                        size="3x"
                      />
                    </div>
                    <div className="remove-text">
                      <p>{errorMsg}</p>
                      <br />
                      <Button className="cancel-btn" onClick={handleClose}>
                        Ok
                      </Button>
                    </div>
                  </div>
                </Modal.Body>
              </Modal>
            </ManageCompanyLayout>
          </Form>
        );
      }}
    </Formik>
  );
};

export default PayrollCalendarContainer;
